import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Calendar, Search, Plus, Edit3, Trash2, Clock, User, MapPin, Filter, RefreshCw } from 'lucide-react';
import EmptyState from '../../../components/ui/EmptyState';


const AppointmentManagement = ({ onStatsUpdate }) => {
  const [appointments, setAppointments] = useState([]);
  const [notaries, setNotaries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    notary_id: 'all',
    agent_id: 'all',
    date_range: 'all'
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);

  // Appointment status options
  const statusOptions = [
    { value: 'scheduled', label: 'Programmé', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'confirmed', label: 'Confirmé', color: 'bg-blue-100 text-blue-800' },
    { value: 'completed', label: 'Terminé', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: 'Annulé', color: 'bg-red-100 text-red-800' },
    { value: 'rescheduled', label: 'Reporté', color: 'bg-purple-100 text-purple-800' }
  ];

  // Fetch appointments with related data
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('appointments')?.select(`
        *,
        agent:user_profiles!agent_id(id, full_name, email, phone),
        client:user_profiles!client_id(id, full_name, email, phone),
        notary:notaries(
          id, 
          license_number, 
          office_address,
          user_profile:user_profiles(full_name, email, phone)
        ),
        property:properties(id, title, address, property_type)
      `)?.order('scheduled_date', { ascending: false });

      // Apply filters
      if (filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.notary_id !== 'all') {
        query = query?.eq('notary_id', filters?.notary_id);
      }
      if (filters?.agent_id !== 'all') {
        query = query?.eq('agent_id', filters?.agent_id);
      }

      // Date range filter
      if (filters?.date_range !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (filters?.date_range) {
          case 'today':
            startDate = new Date(now?.getFullYear(), now?.getMonth(), now?.getDate());
            query = query?.gte('scheduled_date', startDate?.toISOString())?.lt('scheduled_date', new Date(startDate?.getTime() + 24 * 60 * 60 * 1000)?.toISOString());
            break;
          case 'week':
            startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
            query = query?.gte('scheduled_date', startDate?.toISOString());
            break;
          case 'month':
            startDate = new Date(now?.getFullYear(), now?.getMonth(), 1);
            query = query?.gte('scheduled_date', startDate?.toISOString());
            break;
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      setAppointments(data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      alert('Erreur lors du chargement des rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  // Fetch notaries and agents for filters
  const fetchFilterData = async () => {
    try {
      const [notariesRes, agentsRes] = await Promise.all([
        supabase?.from('notaries')?.select(`
          id, 
          license_number,
          user_profile:user_profiles(full_name)
        `)?.order('license_number'),
        supabase?.from('user_profiles')?.select('id, full_name')?.eq('role', 'agent')?.order('full_name')
      ]);

      setNotaries(notariesRes?.data || []);
      setAgents(agentsRes?.data || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [filters]);

  useEffect(() => {
    fetchFilterData();
  }, []);

  // Filter appointments based on search term
  const filteredAppointments = appointments?.filter(appointment => 
    appointment?.property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    appointment?.client?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    appointment?.agent?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    appointment?.notary?.user_profile?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  // Handle appointment deletion
  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) {
      return;
    }

    try {
      const { error } = await supabase?.from('appointments')?.delete()?.eq('id', appointmentId);
      if (error) throw error;

      fetchAppointments();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Erreur lors de la suppression du rendez-vous');
    }
  };

  // Handle status change
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const { error } = await supabase?.from('appointments')?.update({ status: newStatus })?.eq('id', appointmentId);
      if (error) throw error;

      // Send email + SMS notification for status changes that affect the client
      const notifiableStatuses = ['confirmed', 'cancelled', 'rescheduled'];
      if (notifiableStatuses?.includes(newStatus)) {
        const appt = appointments?.find(a => a?.id === appointmentId);
        if (appt) {
          const clientEmail = appt?.client?.email || appt?.client_email;
          const clientPhone = appt?.client?.phone || appt?.client_phone;
          const clientName = appt?.client?.full_name || appt?.client_name;
          if (clientEmail || clientPhone) {
            // Use emailNotificationService which handles both email + SMS
            const TRANSACTIONAL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;
            const SMS_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms-confirmation`;
            const { data: sessionData } = await supabase?.auth?.getSession();
            const token = sessionData?.session?.access_token;
            const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
            const payload = {
              type: 'appointment_status_update',
              data: {
                client_name: clientName,
                client_email: clientEmail,
                client_phone: clientPhone,
                appointment_type: appt?.appointment_type,
                scheduled_date: appt?.scheduled_date,
                scheduled_time: appt?.scheduled_time,
                new_status: newStatus,
                notary_name: appt?.notary?.user_profile?.full_name || appt?.notary?.notary_name,
                location: appt?.location || appt?.notary?.office_address,
              },
            };
            // Fire-and-forget
            if (clientEmail) {
              fetch(TRANSACTIONAL_URL, { method: 'POST', headers, body: JSON.stringify(payload) })?.catch(e => console.warn('[AppointmentMgmt] Email notification failed:', e));
            }
            if (clientPhone) {
              fetch(SMS_URL, { method: 'POST', headers, body: JSON.stringify(payload) })?.catch(e => console.warn('[AppointmentMgmt] SMS notification failed:', e));
            }
          }
        }
      }

      fetchAppointments();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Get status color class
  const getStatusColor = (status) => {
    const option = statusOptions?.find(opt => opt?.value === status);
    return option?.color || 'bg-gray-100 text-gray-800';
  };

  // Get status label
  const getStatusLabel = (status) => {
    const option = statusOptions?.find(opt => opt?.value === status);
    return option?.label || status;
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date?.toLocaleDateString('fr-FR'),
      time: date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Get appointment type color
  const getAppointmentTypeColor = (type) => {
    const colors = {
      'Property Visit': 'bg-blue-50 text-blue-700',
      'Document Signing': 'bg-green-50 text-green-700',
      'Consultation': 'bg-purple-50 text-purple-700',
      'Final Closing': 'bg-orange-50 text-orange-700'
    };
    return colors?.[type] || 'bg-gray-50 text-gray-700';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="bg-card border border-border p-6 rounded-lg animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-muted rounded-lg"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-card border border-border p-4 sm:p-6 rounded-lg">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Calendar className="w-5 h-5 text-brand-gold flex-shrink-0" />
                Gestion des RDV Notaire
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {filteredAppointments?.length} rendez-vous trouvé(s)
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={fetchAppointments}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-muted border border-border text-foreground rounded-lg hover:bg-muted/80 transition-colors min-h-[44px] touch-manipulation text-sm"
              >
                <RefreshCw className="w-4 h-4 flex-shrink-0" />
                <span>Actualiser</span>
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-lg hover:bg-brand-earth/90 transition-colors min-h-[44px] touch-manipulation text-sm font-medium"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span>Nouveau RDV</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher RDV..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Statut</label>
              <select
                value={filters?.status}
                onChange={(e) => setFilters({...filters, status: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Tous les statuts</option>
                {statusOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notaire</label>
              <select
                value={filters?.notary_id}
                onChange={(e) => setFilters({...filters, notary_id: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Tous les notaires</option>
                {notaries?.map(notary => (
                  <option key={notary?.id} value={notary?.id}>
                    {notary?.user_profile?.full_name} ({notary?.license_number})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Agent</label>
              <select
                value={filters?.agent_id}
                onChange={(e) => setFilters({...filters, agent_id: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Tous les agents</option>
                {agents?.map(agent => (
                  <option key={agent?.id} value={agent?.id}>{agent?.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Période</label>
              <select
                value={filters?.date_range}
                onChange={(e) => setFilters({...filters, date_range: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Toutes les dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Appointments Table — scrollable on mobile */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border" style={{ minWidth: '700px' }}>
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  RDV Info
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                  Propriété
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Client
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                  Notaire
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Statut
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filteredAppointments?.map((appointment) => {
                const dateTime = formatDateTime(appointment?.scheduled_date);
                return (
                  <tr key={appointment?.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm font-medium text-foreground">{dateTime?.date}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {dateTime?.time} ({appointment?.duration_minutes}min)
                        </div>
                        <div className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mt-1.5 ${getAppointmentTypeColor(appointment?.appointment_type)}`}>
                          {appointment?.appointment_type}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div>
                        <div className="text-sm font-medium text-foreground max-w-[140px] truncate">
                          {appointment?.property?.title}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate max-w-[120px]">{appointment?.property?.address}</span>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-brand-gold/10 border border-brand-gold/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-3.5 h-3.5 text-brand-gold" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate max-w-[100px] sm:max-w-none">
                            {appointment?.client?.full_name}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-none hidden sm:block">
                            {appointment?.client?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {appointment?.notary?.user_profile?.full_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {appointment?.notary?.license_number}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <select
                        value={appointment?.status}
                        onChange={(e) => handleStatusChange(appointment?.id, e?.target?.value)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border-none cursor-pointer min-h-[28px] ${getStatusColor(appointment?.status)}`}
                      >
                        {statusOptions?.map(option => (
                          <option key={option?.value} value={option?.value}>{option?.label}</option>
                        ))}
                      </select>
                    </td>
                    
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setEditingAppointment(appointment); setShowAddModal(true); }}
                          className="text-brand-earth hover:text-brand-earth/80 p-2 rounded-lg hover:bg-brand-earth/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center touch-manipulation"
                          aria-label="Modifier"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAppointment(appointment?.id)}
                          className="text-destructive hover:text-destructive/80 p-2 rounded-lg hover:bg-destructive/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center touch-manipulation"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredAppointments?.length === 0 && (
            <EmptyState
              iconName="CalendarOff"
              title="Aucun rendez-vous trouvé"
              description="Aucun rendez-vous ne correspond aux filtres sélectionnés. Programmez un nouveau rendez-vous."
              ctaLabel="Programmer un RDV"
              ctaIcon="Plus"
              onCta={() => setShowAddModal(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentManagement;